import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSeoMeta, useSocialMeta } from '@/hooks/usePageTitle';
import { AppShell } from '@/components/AppShell';
import { CompanyProfile } from '@/components/calculator/CompanyProfile';
import { SecurityMaturity } from '@/components/calculator/SecurityMaturity';
import { ScopeInfrastructure } from '@/components/calculator/ScopeInfrastructure';
import { ImplementationApproach } from '@/components/calculator/ImplementationApproach';
import { AdvancedOptions } from '@/components/calculator/AdvancedOptions';
import { ResultsPanel } from '@/components/calculator/ResultsPanel';
import { BenchmarkComparison } from '@/components/calculator/BenchmarkComparison';
import { SensitivityPanel } from '@/components/calculator/results/SensitivityPanel';
import { TcoPanel } from '@/components/calculator/results/TcoPanel';
import { GanttView } from '@/components/calculator/GanttView';
import { PresetBar } from '@/components/calculator/PresetBar';
import { calculateEstimate } from '@/lib/calculationEngine';
import { CURRENCIES, ISO_2022_GROUPS } from '@/lib/calculationEngine';
import { Info, RotateCcw, TrendingUp, Clock, ChevronUp, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/hooks/useLocale';
import { useCalculatorLandingEntry } from '@/lib/landingAnalytics';
import { useCalculatorCompletion } from '@/lib/calculatorAnalytics';

// Default state
const defaultConfig = {
  companySize: 'medium', industry: 'technology', locations: 1, itSystems: 10,
  maturityMode: 'simple', overallMaturity: 2,
  // Per ISO 27001:2022 advanced assessment control groups (neutral baseline = 3).
  domainMaturity: Object.fromEntries(ISO_2022_GROUPS.map(g => [g.id, 3])),
  infraType: 'hybrid', existingCerts: [],
  approach: 'mixed', internalSplit: 50, mixRatio: 50,
  currency: 'EUR', internalRate: 600, consultantRate: 1200,
  // Optional organisation-specific planning inputs (raise estimate confidence).
  employees: null, targetMonths: null,
};

export default function CalculatorPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { lang, localePath } = useLocale();
  useCalculatorLandingEntry(lang);
  useSeoMeta(t('seo.pages.calculatorTitle'), t('seo.pages.calculatorDescription'));
  useSocialMeta({
    title: t('seo.pages.calculatorTitle'),
    description: t('seo.pages.calculatorDescription'),
    url: `https://ismscalculator.com/${lang}/calculator`,
  });

  const handleDemoSignup = async () => {
    await logout();
    navigate(localePath('/auth'));
  };
  const assessmentScores = location.state?.maturityScores;
  const estimateConfig = location.state?.estimateConfig;
  const estimateName = location.state?.estimateName;
  const resultsRef = useRef(null);

  const [config, setConfig] = useState(() => {
    if (estimateConfig) return { ...defaultConfig, ...estimateConfig };
    if (assessmentScores) return { ...defaultConfig, maturityMode: 'advanced', domainMaturity: assessmentScores };
    return defaultConfig;
  });

  const [activePreset, setActivePreset] = useState(null);
  // Track whether we're viewing a loaded estimate
  const [loadedEstimateName, setLoadedEstimateName] = useState(estimateName || null);
  // Mobile sticky bar visibility (shown after scrolling past results)
  const [showStickyBar, setShowStickyBar] = useState(false);
  // Recurring assumptions for the 3-Year Planning Outlook (server-calculated).
  const [tcoOverrides, setTcoOverrides] = useState(() => location.state?.tcoAssumptions || {});
  const [tcoData, setTcoData] = useState(null);
  // A configuration handed over from a saved estimate or the assessment is
  // already tailored, so it does not need an edit on this page to count.
  const { markEdit, markAuthoritativeResult } = useCalculatorCompletion(
    Boolean(estimateConfig || assessmentScores),
  );

  const handleTcoData = useCallback((data) => {
    setTcoData(data);
    // The outlook response is the server cost engine validating this exact
    // configuration, so it is the authoritative confirmation of a completion.
    markAuthoritativeResult();
  }, [markAuthoritativeResult]);

  // Show sticky bar once user scrolls past the results panel on mobile
  useEffect(() => {
    const el = resultsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { setShowStickyBar(!entry.isIntersecting); },
      { threshold: 0, rootMargin: '-100px 0px 0px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const updateConfig = useCallback((updates) => {
    setConfig(prev => ({ ...prev, ...updates }));
    setActivePreset(null);
    markEdit();
  }, [markEdit]);

  const handlePresetSelect = useCallback((presetId, presetConfig) => {
    setActivePreset(presetId);
    if (presetId && presetConfig) {
      setConfig(prev => ({ ...prev, ...presetConfig }));
      setLoadedEstimateName(null);
      markEdit();
    }
  }, [markEdit]);

  const handleReset = useCallback(() => {
    setConfig(defaultConfig);
    setActivePreset(null);
    setLoadedEstimateName(null);
    setTcoOverrides({});
  }, []);

  // Calculate results whenever config changes
  const results = useMemo(() => calculateEstimate(config), [config]);

  const currencyObj = CURRENCIES.find(c => c.value === config.currency);
  const symbol = currencyObj?.symbol || '€';
  const formatK = (v) => v >= 1000 ? `${symbol}${Math.round(v / 1000)}k` : `${symbol}${v}`;
  const formatCost = (v) => `${symbol}${Number(v || 0).toLocaleString()}`;

  const resetAction = (
    <button
      onClick={handleReset}
      className="shrink-0 flex items-center gap-1.5 text-xs text-[#66707A] hover:text-[#171A1D] border border-[#DDE1E5] bg-white rounded-md px-3 py-2 transition-colors duration-150"
      data-testid="reset-calculator-btn"
      aria-label="Reset to default settings and start a new estimate"
      title="Reset to defaults"
    >
      <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
      <span className="hidden sm:inline">{t('estimates.newEstimate')}</span>
    </button>
  );

  return (
    <AppShell title={t('calculator.title')} subtitle={t('calculator.subtitle')} actions={resetAction}>
      <div>
        <div className="max-w-[1400px] mx-auto pb-24 lg:pb-4">
          <div className="mb-5">
            {loadedEstimateName && (
              <div className="mt-3 flex items-center gap-2 text-xs text-[#2867B2] bg-[#2867B2]/10 px-3 py-1.5 rounded-lg w-fit" data-testid="loaded-estimate-banner" role="status">
                <Info className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                Loaded: <span className="font-semibold">{loadedEstimateName}</span>
                <button
                  onClick={handleReset}
                  className="ml-1 text-[#2867B2]/60 hover:text-[#2867B2] transition-colors"
                  data-testid="dismiss-loaded-banner"
                  aria-label="Dismiss loaded estimate banner"
                >
                  ✕
                </button>
              </div>
            )}
            {assessmentScores && !loadedEstimateName && (
              <div className="mt-3 flex items-center gap-2 text-xs text-[#527F19] bg-[#527F19]/10 px-3 py-1.5 rounded-lg w-fit" data-testid="prefilled-banner" role="status">
                <Info className="w-3.5 h-3.5" aria-hidden="true" /> {t('calculator.prefilled')}
              </div>
            )}
          </div>

          {/* Demo → sign-up conversion banner */}
          {user?.is_demo && (
            <div
              className="mb-5 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-[#B7791F]/40 bg-[#FFFBEB] px-4 py-3"
              data-testid="demo-conversion-banner"
              role="status"
            >
              <div className="flex items-start gap-2.5 flex-1">
                <Sparkles className="w-4 h-4 text-[#B7791F] shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-sm text-[#8A5A0B]">{t('calculator.demoBanner')}</p>
              </div>
              <button
                onClick={handleDemoSignup}
                className="shrink-0 rounded-lg bg-[#527F19] hover:bg-[#3F6114] text-white text-sm font-semibold px-4 py-2 transition-colors shadow-card"
                data-testid="demo-banner-signup-btn"
              >
                {t('calculator.demoBannerCta')}
              </button>
            </div>
          )}

          {/* Quick Preset Bar */}
          <PresetBar activePreset={activePreset} onPresetSelect={handlePresetSelect} />

          <div className="calculator-grid" data-testid="calculator-workspace">
            {/* Left: Input panels — shown second on mobile, first on desktop */}
            <div className="calculator-inputs" role="form" aria-label={t('calculator.title')}>
              <CompanyProfile config={config} updateConfig={updateConfig} />
              <SecurityMaturity config={config} updateConfig={updateConfig} />
              <ScopeInfrastructure config={config} updateConfig={updateConfig} />
              <ImplementationApproach config={config} updateConfig={updateConfig} />
              <AdvancedOptions results={results} />
              <BenchmarkComparison results={results} config={config} />
            </div>
            {/* Right: Results — shown first on mobile, sticky on desktop */}
            <div ref={resultsRef} className="calculator-results">
              <ResultsPanel
                results={results}
                config={config}
                loadedEstimateName={loadedEstimateName}
                onEditAssumption={updateConfig}
                tco={tcoData}
                tcoOverrides={tcoOverrides}
              />
            </div>
          </div>

          {/* Full-width Gantt / Roadmap below the grid */}
          <div className="mt-8 sm:mt-10">
            <GanttView results={results} />
          </div>

          {/* Cost sensitivity + 3-Year Planning Outlook (both server-authoritative) */}
          <div className="mt-6 space-y-6">
            <SensitivityPanel config={config} formatCost={formatCost} />
            <TcoPanel
              config={config}
              overrides={tcoOverrides}
              onOverridesChange={setTcoOverrides}
              onData={handleTcoData}
              formatCost={formatCost}
            />
          </div>
        </div>
      </div>

      {/* Mobile sticky bottom summary bar — visible only on mobile when results panel is out of view */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-40 lg:hidden mobile-summary-bar transition-transform duration-300 ${
          showStickyBar ? 'translate-y-0' : 'translate-y-full'
        }`}
        data-testid="mobile-sticky-bar"
        aria-live="polite"
        aria-atomic="true"
        aria-label="Current estimate summary"
      >
        <div className="bg-[#171A1D] border-t border-[#2A2F34] shadow-raised">
          <div className="flex items-center justify-between px-4 py-3 gap-3">
            <div className="flex items-center gap-4 min-w-0">
              <div className="flex items-center gap-1.5 text-white min-w-0">
                <TrendingUp className="w-3.5 h-3.5 text-[#527F19] shrink-0" aria-hidden="true" />
                <span className="text-xs text-[#8A929B] shrink-0">{t('estimates.costLabel')}</span>
                <span className="font-bold text-sm ml-1 truncate" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                  {formatK(results.costRange.low)}–{formatK(results.costRange.high)}
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-white shrink-0">
                <Clock className="w-3.5 h-3.5 text-[#8A929B] shrink-0" aria-hidden="true" />
                <span className="font-bold text-sm" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                  {results.totalDurationWeeks}w
                </span>
              </div>
            </div>
            <button
              onClick={() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="shrink-0 flex items-center gap-1 text-xs text-[#527F19] hover:text-[#78B928] transition-colors font-semibold"
              aria-label="Scroll up to view full results"
              data-testid="mobile-sticky-view-results"
            >
              <ChevronUp className="w-3.5 h-3.5" aria-hidden="true" />
              {t('results.viewResults')}
            </button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
